import { useStores } from 'Hooks/useStore';
import { useIsLMSModulesHidden } from 'Hooks';
import { useCallback, useMemo, useEffect, useState } from 'react';
import { NavItemConfig, mainNavItems, accountItems, PEAKPORTAL_KEY } from '../Siderbar.constant';
import { PermissionCode } from 'Services/Api/Users/Types';
import Icon from 'Components/Icon/Icon';
import { SystemRoleType } from 'Services/Api/Roles/Types';
import { useIsMobile } from 'Hooks';

export const useMenuItems = () => {
    const [menuItems, setMenuItems] = useState<NavItemConfig[]>(mainNavItems);
    const { systemsStore, authStore : { currentUserPermissionCodes, currentUser } } = useStores();
    const isMobile = useIsMobile();
    const isModulesNavHidden = useIsLMSModulesHidden();
    const userRole = currentUser?.primarySystemRole?.role || "";

    useEffect(() => {
        async function getSystems() {
            if(!systemsStore.systems.length) await systemsStore.loadSystems();
            if (systemsStore.systems.length && !isModulesNavHidden && menuItems[menuItems.length - 1].key != "modules") {
                setMenuItems((menuItems) => {
                    return menuItems.concat({
                        key: 'modules',
                        label: 'sidebar.modules',
                        icon: <Icon type="modulesIcon" />,
						children: systemsStore.systems.map((system) => ({
                            key: PEAKPORTAL_KEY,
                            label: system.systemName
                        }))
                    });
                });
            }
        }
        getSystems();
    }, []);

    const getIsMenuItemVisible = useCallback((menuItem: NavItemConfig, isMobile: boolean) => {
        const isEndUser = userRole === SystemRoleType.User;
        const allowedRoutes = [PEAKPORTAL_KEY, PermissionCode.VIEW_MY_COURCES];
        if(userRole && !isEndUser) {
            allowedRoutes.push(PermissionCode.VIEW_ASSIGNMENTS);
            allowedRoutes.push(PermissionCode.VIEW_WHITELISTING);
            allowedRoutes.push(PermissionCode.VIEW_POLICIES);
        }

        if(isMobile && menuItem.key === PermissionCode.ViewAuditLog) return false;

        // Assignments under Training is for elevated roles only, even when an end
        // user happens to carry the permission code.
        if(isEndUser && menuItem.key === PermissionCode.VIEW_ASSIGNMENTS) return false;

        return (!menuItem.key || allowedRoutes.includes(menuItem.key) || currentUserPermissionCodes.includes((menuItem.key as PermissionCode) || ''));
    }, [currentUserPermissionCodes, userRole]);

    const calculatedMenuItems = useMemo(() => {
        return menuItems.filter(item => !(isMobile && item.key === "settings")).reduce<NavItemConfig[]>((acc, value) => {
            if (value.children) {
                const children = value.children.filter((child) => {
                    child["label"] = (!isModulesNavHidden && child.label === "sidebar.settings.groups") ? "sidebar.settings.tenants" : child.label;
                    return getIsMenuItemVisible(child, isMobile);
                });
                if (children.length) acc.push({ ...value, children });
            } else {
                if (getIsMenuItemVisible(value, isMobile)) acc.push(value);
            }
            return acc;
        }, []);
    }, [getIsMenuItemVisible, menuItems, isMobile]);

    return { menuItems: calculatedMenuItems };
};
